<script setup lang="ts">
  import dayjs, { type Dayjs } from "dayjs";
  import { computed, nextTick, ref, useId, useTemplateRef, watch } from "vue";

  export interface CalendarRange {
    start: Date | null;
    end: Date | null;
  }

  type CalendarMode = "single" | "range";

  interface Props {
    modelValue?: Date | null;
    range?: CalendarRange | null;
    mode?: CalendarMode;
    locale?: string;
    disabledDates?: Date[];
    minDate?: Date;
    maxDate?: Date;
  }

  const props = withDefaults(defineProps<Props>(), {
    modelValue: null,
    range: null,
    mode: "single",
    locale: "en-US",
    disabledDates: () => [],
    minDate: undefined,
    maxDate: undefined,
  });

  const emit = defineEmits<{
    "update:modelValue": [value: Date | null];
    "update:range": [value: CalendarRange];
    change: [value: Date];
  }>();

  const DAYS_IN_WEEK = 7;
  const WEEKS_IN_GRID = 6;
  // 2024-01-01 was a Monday — a stable anchor for building Mon→Sun headers.
  const WEEK_ANCHOR_MONDAY = dayjs("2024-01-01");

  // dayjs owns every date calculation/comparison below. Localized *labels*
  // (weekday and month names) are produced with Intl so any BCP-47 `locale`
  // works without having to bundle a dayjs locale module per language.
  const longDateFormatter = computed(
    () =>
      new Intl.DateTimeFormat(props.locale, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
  );
  const monthYearFormatter = computed(
    () => new Intl.DateTimeFormat(props.locale, { month: "long", year: "numeric" }),
  );
  const weekdayShortFormatter = computed(
    () => new Intl.DateTimeFormat(props.locale, { weekday: "short" }),
  );
  const weekdayLongFormatter = computed(
    () => new Intl.DateTimeFormat(props.locale, { weekday: "long" }),
  );

  const today = dayjs();
  const selected = computed(() => (props.modelValue ? dayjs(props.modelValue) : null));
  const rangeStart = computed(() => (props.range?.start ? dayjs(props.range.start) : null));
  const rangeEnd = computed(() => (props.range?.end ? dayjs(props.range.end) : null));

  // Open on whichever value is provided, falling back to today.
  const initialDate = selected.value ?? rangeStart.value ?? today;
  // The month currently rendered in the grid.
  const viewDate = ref<Dayjs>(initialDate.startOf("month"));
  // The single day reachable via Tab (roving tabindex); arrow keys move it.
  const focusedDate = ref<Dayjs>(initialDate);

  const gridRef = useTemplateRef<HTMLElement>("gridRef");
  const captionId = useId();

  // Move the view (and roving focus) when the bound value is changed externally.
  watch(
    () => props.modelValue,
    (value) => {
      if (!value) return;
      const next = dayjs(value);
      viewDate.value = next.startOf("month");
      focusedDate.value = next;
    },
  );

  const disabledSet = computed(
    () => new Set(props.disabledDates.map((date) => dayjs(date).format("YYYY-MM-DD"))),
  );

  function isDisabled(date: Dayjs): boolean {
    if (props.minDate && date.isBefore(dayjs(props.minDate), "day")) return true;
    if (props.maxDate && date.isAfter(dayjs(props.maxDate), "day")) return true;
    return disabledSet.value.has(date.format("YYYY-MM-DD"));
  }

  const caption = computed(() => monthYearFormatter.value.format(viewDate.value.toDate()));

  const weekdays = computed(() =>
    Array.from({ length: DAYS_IN_WEEK }, (_, index) => {
      const day = WEEK_ANCHOR_MONDAY.add(index, "day").toDate();
      return {
        short: weekdayShortFormatter.value.format(day),
        long: weekdayLongFormatter.value.format(day),
      };
    }),
  );

  interface CalendarDay {
    key: string;
    date: Dayjs;
    label: number;
    inCurrentMonth: boolean;
    isToday: boolean;
    isSelected: boolean;
    isInRange: boolean;
    isRangeStart: boolean;
    isRangeEnd: boolean;
    inRangeBar: boolean;
    isDisabled: boolean;
    isFocusable: boolean;
    ariaLabel: string;
  }

  const weeks = computed<CalendarDay[][]>(() => {
    const monthStart = viewDate.value.startOf("month");
    // Monday-based offset: dayjs day() is 0=Sun..6=Sat.
    const leadingDays = (monthStart.day() + 6) % 7;
    const gridStart = monthStart.subtract(leadingDays, "day");

    const rangeMode = props.mode === "range";
    const start = rangeStart.value;
    const end = rangeEnd.value;
    const hasFullRange = rangeMode && start !== null && end !== null;

    const result: CalendarDay[][] = [];
    for (let week = 0; week < WEEKS_IN_GRID; week += 1) {
      const days: CalendarDay[] = [];
      for (let weekday = 0; weekday < DAYS_IN_WEEK; weekday += 1) {
        const date = gridStart.add(week * DAYS_IN_WEEK + weekday, "day");

        const isStart = rangeMode && start !== null && date.isSame(start, "day");
        const isEnd = rangeMode && end !== null && date.isSame(end, "day");
        const isInRange = hasFullRange && date.isAfter(start, "day") && date.isBefore(end, "day");
        const isSingleSelected =
          !rangeMode && selected.value !== null && date.isSame(selected.value, "day");

        days.push({
          key: date.format("YYYY-MM-DD"),
          date,
          label: date.date(),
          inCurrentMonth: date.isSame(monthStart, "month"),
          isToday: date.isSame(today, "day"),
          isSelected: isSingleSelected || isStart || isEnd,
          isInRange,
          // The connecting bar (and its rounded ends) only show once both
          // bounds are chosen.
          isRangeStart: hasFullRange && isStart,
          isRangeEnd: hasFullRange && isEnd,
          inRangeBar: hasFullRange && (isInRange || isStart || isEnd),
          isDisabled: isDisabled(date),
          isFocusable: date.isSame(focusedDate.value, "day"),
          ariaLabel: longDateFormatter.value.format(date.toDate()),
        });
      }
      result.push(days);
    }
    return result;
  });

  function goToMonth(month: Dayjs) {
    viewDate.value = month.startOf("month");
    // Keep the roving focus inside the visible month.
    const day = Math.min(focusedDate.value.date(), viewDate.value.daysInMonth());
    focusedDate.value = viewDate.value.date(day);
  }

  function goToPreviousMonth() {
    goToMonth(viewDate.value.subtract(1, "month"));
  }

  function goToNextMonth() {
    goToMonth(viewDate.value.add(1, "month"));
  }

  function focusActiveCell() {
    gridRef.value?.querySelector<HTMLElement>('[data-active="true"]')?.focus();
  }

  async function moveFocusTo(date: Dayjs) {
    focusedDate.value = date;
    if (!date.isSame(viewDate.value, "month")) {
      viewDate.value = date.startOf("month");
    }
    await nextTick();
    focusActiveCell();
  }

  function select(day: CalendarDay) {
    if (day.isDisabled) return;
    focusedDate.value = day.date;
    if (!day.date.isSame(viewDate.value, "month")) {
      viewDate.value = day.date.startOf("month");
    }

    if (props.mode === "range") {
      const clicked = day.date.startOf("day");
      const start = rangeStart.value;
      const end = rangeEnd.value;
      // Begin a fresh range when nothing is started or a full range already exists.
      if (start === null || end !== null) {
        emit("update:range", { start: clicked.toDate(), end: null });
        return;
      }
      // A start exists with an open end: close the range, keeping bounds ordered.
      const next: CalendarRange = clicked.isBefore(start, "day")
        ? { start: clicked.toDate(), end: start.toDate() }
        : { start: start.toDate(), end: clicked.toDate() };
      emit("update:range", next);
      return;
    }

    const date = day.date.startOf("day").toDate();
    emit("update:modelValue", date);
    emit("change", date);
  }

  function onKeydown(event: KeyboardEvent) {
    if (event.ctrlKey || event.metaKey || event.altKey) return;

    const current = focusedDate.value;
    let next: Dayjs | null = null;

    switch (event.key) {
      case "ArrowLeft":
        next = current.subtract(1, "day");
        break;
      case "ArrowRight":
        next = current.add(1, "day");
        break;
      case "ArrowUp":
        next = current.subtract(1, "week");
        break;
      case "ArrowDown":
        next = current.add(1, "week");
        break;
      case "Home":
        // Start of the week (Monday).
        next = current.subtract((current.day() + 6) % 7, "day");
        break;
      case "End":
        // End of the week (Sunday).
        next = current.add(6 - ((current.day() + 6) % 7), "day");
        break;
      case "PageUp":
        // dayjs clamps month overflow (e.g. Mar 31 → Feb 28/29).
        next = current.subtract(1, "month");
        break;
      case "PageDown":
        next = current.add(1, "month");
        break;
      default:
        // Enter / Space fall through to the native button's click handler.
        return;
    }

    event.preventDefault();
    void moveFocusTo(next);
  }
</script>

<template>
  <div :class="$style.calendar">
    <div :class="$style.header">
      <button
        type="button"
        :class="$style.navButton"
        aria-label="Previous month"
        @click="goToPreviousMonth"
      >
        <svg :class="$style.navIcon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M15 18l-6-6 6-6"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
      <div :id="captionId" :class="$style.caption" aria-live="polite">{{ caption }}</div>
      <button
        type="button"
        :class="$style.navButton"
        aria-label="Next month"
        @click="goToNextMonth"
      >
        <svg :class="$style.navIcon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M9 6l6 6-6 6"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>

    <div
      ref="gridRef"
      :class="$style.grid"
      role="grid"
      :aria-labelledby="captionId"
      @keydown="onKeydown"
    >
      <div :class="$style.weekdays" role="row">
        <span
          v-for="weekday in weekdays"
          :key="weekday.long"
          :class="$style.weekday"
          role="columnheader"
          :aria-label="weekday.long"
        >
          {{ weekday.short }}
        </span>
      </div>

      <div v-for="(week, weekIndex) in weeks" :key="weekIndex" :class="$style.weekRow" role="row">
        <div
          v-for="day in week"
          :key="day.key"
          :class="[
            $style.cell,
            {
              [$style.cellRange]: day.inRangeBar,
              [$style.cellRangeStart]: day.isRangeStart,
              [$style.cellRangeEnd]: day.isRangeEnd,
            },
          ]"
          role="gridcell"
          :aria-selected="day.isSelected || day.isInRange"
        >
          <button
            type="button"
            :class="[
              $style.day,
              {
                [$style.outside]: !day.inCurrentMonth,
                [$style.today]: day.isToday,
                [$style.selected]: day.isSelected,
                [$style.disabled]: day.isDisabled,
              },
            ]"
            :tabindex="day.isFocusable ? 0 : -1"
            :data-active="day.isFocusable"
            :aria-label="day.ariaLabel"
            :aria-current="day.isToday ? 'date' : undefined"
            :aria-disabled="day.isDisabled || undefined"
            @click="select(day)"
          >
            <span :class="$style.dayLabel">{{ day.label }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
  .calendar {
    // Map the app theme tokens to local variables (with standalone-friendly
    // fallbacks). Light/dark switching is handled by whatever sets these on
    // an ancestor; the calendar simply follows.
    --cal-foreground: var(--foreground, #e8e8e8);
    --cal-muted: var(--muted-foreground, #8a8a8a);
    --cal-surface: var(--popover, #1f1f1f);
    --cal-accent-bg: var(--secondary, rgb(255 255 255 / 8%));
    --cal-border: var(--border, rgb(255 255 255 / 10%));
    --cal-selected: var(--brand, #facc15);
    --cal-selected-foreground: var(--brand-foreground, #1a1a1a);
    --cal-radius: var(--radius, 0.625rem);
    --cal-cell-radius: calc(var(--cal-radius) - 0.25rem);

    width: 100%;
    min-width: 0;
    max-width: 20rem;
    padding: 0.75rem;

    background-color: var(--ksd-bg-color);
    color: var(--cal-foreground);
    // border: 1px solid var(--cal-border);
    border-radius: var(--cal-radius);

    font-family: inherit;
    user-select: none;
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    padding: 0.25rem 0;
    margin-bottom: 0.25rem;
  }

  .caption {
    flex: 1;

    font-size: 0.875rem;
    font-weight: 500;
    text-align: center;
    text-transform: capitalize;
  }

  .navButton {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    padding: 0;

    color: var(--cal-foreground);
    background: transparent;
    border: 1px solid var(--cal-border);
    border-radius: var(--cal-cell-radius);
    opacity: 0.5;
    cursor: pointer;

    transition:
      background-color 0.15s ease,
      opacity 0.15s ease;

    &:hover {
      opacity: 1;
      background-color: var(--cal-accent-bg);
    }

    &:focus-visible {
      opacity: 1;
      outline: 2px solid var(--cal-selected);
      outline-offset: 2px;
    }
  }

  .navIcon {
    display: block;
    width: 1rem;
    height: 1rem;
  }

  .grid {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  // Days sit flush (no horizontal gap) so a selected range reads as one bar.
  .weekdays,
  .weekRow {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }

  .weekday {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 2.25rem;

    color: var(--cal-muted);
    font-size: 0.8rem;
    font-weight: 400;
    text-transform: capitalize;
  }

  .cell {
    display: flex;
    min-width: 0;
  }

  // The connecting range bar lives on the cell so adjacent days join up.
  .cellRange {
    background-color: var(--cal-accent-bg);
  }

  .cellRange:first-child,
  .cellRangeStart {
    border-top-left-radius: var(--cal-cell-radius);
    border-bottom-left-radius: var(--cal-cell-radius);
  }

  .cellRange:last-child,
  .cellRangeEnd {
    border-top-right-radius: var(--cal-cell-radius);
    border-bottom-right-radius: var(--cal-cell-radius);
  }

  .day {
    position: relative;

    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    aspect-ratio: 1 / 1;
    padding: 0;

    color: var(--cal-foreground);
    background: transparent;
    border: none;
    border-radius: var(--cal-cell-radius);
    cursor: pointer;

    font-size: 0.875rem;
    font-weight: 400;
    font-variant-numeric: tabular-nums;

    transition:
      background-color 0.15s ease,
      color 0.15s ease;

    &:hover {
      background-color: var(--cal-accent-bg);
    }

    &:focus-visible {
      z-index: 1;
      outline: 2px solid var(--cal-selected);
      outline-offset: -2px;
    }
  }

  .dayLabel {
    line-height: 1;
  }

  .outside {
    color: var(--cal-muted);
    opacity: 0.5;
  }

  .today:not(.selected) {
    background-color: var(--cal-accent-bg);
  }

  .selected {
    color: var(--cal-selected-foreground);
    background-color: var(--cal-selected);
    font-weight: 500;

    &:hover {
      color: var(--cal-selected-foreground);
      background-color: var(--cal-selected);
    }
  }

  .disabled {
    color: var(--cal-muted);
    opacity: 0.4;
    cursor: not-allowed;

    &:hover {
      background-color: transparent;
    }
  }
</style>
